import {observer} from 'mobx-react-lite'
import React, {useCallback} from 'react'
import tw from 'tailwind-styled-components'
import {DeepReadonly} from 'ts-essentials'
import {Lang} from 'yougile-lang'
import {
  AllNotificationActions,
  ChatNotificationAction,
  getNotificationSetting,
  NotificationChannel,
  notificationChannels,
  NotificationSettings,
  TaskNotificationAction
} from 'yougile-model'
import {Checkbox} from '@components/common/base/checkbox'
import {
  Switcher,
  UncontrolledSwitcher
} from '@components/common/base/switcher/switcher'
import {
  useNotificationSettings,
  useSyncEventUpdateEffect
} from '@containers/hooks'
import {ChatSettings} from '@extensions/chat-settings'
import {globals} from '@extensions/globals-base'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'

const TwTable = tw.div`
  flex
  flex-col
  max-w-[800px]
  min-w-[450px]
  select-none
`

const TwTitle = tw.div`
  flex
  items-center
  text-primary
  text-20
  leading-24
  font-semibold
  mb-24
  mt-16
`

const TwDivider = tw.div`
  border-border-base
  border-0
  border-b-1
  border-solid
  my-24
  w-full
`

const TwHeader = tw.div`
  flex
  items-center
  text-placeholder
  text-10
  font-semibold
  leading-4
  uppercase
  py-4
  gap-8
`

const TwRow = tw.div`
  flex
  items-center
  h-40
  gap-8
`

const TwHeaderCol = tw.div<{$isMain?: boolean}>`
  first:justify-start
  first:flex-1
  flex
  items-center
  justify-center
  text-center
  w-96
  h-48
  py-8
  px-12
  border-none
`

const TwCol = tw.div<{$isMain?: boolean}>`
  first:flex-1
  first:justify-start
  first:w-auto
  first:py-8
  flex
  items-center
  justify-center
  text-primary
  w-96
  h-45
  px-12
  border-0
  ${({$isMain}) => $isMain && 'border-r-1 border-solid border-border-base'}
`

interface NotificationChannelProps {
  channel: NotificationChannel
  action: AllNotificationActions
  getSetting: (
    action: AllNotificationActions,
    channel: NotificationChannel
  ) => boolean
  updateSetting: (
    action: AllNotificationActions,
    channel: NotificationChannel
  ) => () => void
  isDisabled: (
    action: AllNotificationActions,
    channel: NotificationChannel
  ) => boolean
}

const NotificationChannelComponent = observer<NotificationChannelProps>(
  ({channel, action, getSetting, updateSetting, isDisabled}) => {
    const isMain = channel === 'chat'
    const isMainEnabled = getSetting(action, 'chat')
    const isDisabledByMain = !isMainEnabled && !isMain
    const value = getSetting(action, channel) && !isDisabledByMain
    const originalOnChange = updateSetting(action, channel)

    const onChange = (): void => {
      const newValue = !value
      originalOnChange()
      if (isMain) {
        for (const currentChannel of notificationChannels) {
          const channelValue = getSetting(action, currentChannel)
          if (channelValue !== newValue && currentChannel !== channel) {
            updateSetting(action, currentChannel)()
          }
        }
      }
    }

    const isSettingDisabled = isDisabled(action, channel) || isDisabledByMain

    return (
      <TwCol $isMain={isMain}>
        {isMain ? (
          <UncontrolledSwitcher
            active={value}
            onClick={onChange}
            disabled={isSettingDisabled}
          />
        ) : (
          <Checkbox
            value={value}
            onChange={onChange}
            disabled={isSettingDisabled}
          />
        )}
      </TwCol>
    )
  }
)

const taskSettingRow =
  (
    getSetting: (
      action: AllNotificationActions,
      channel: NotificationChannel
    ) => boolean,
    updateSetting: (
      action: AllNotificationActions,
      channel: NotificationChannel
    ) => () => void,
    isDisabled: (
      action: AllNotificationActions,
      channel: NotificationChannel
    ) => boolean
  ) =>
  ({action, title}: {action: AllNotificationActions; title: string}) => {
    return (
      <TwRow>
        <TwCol>{title}</TwCol>
        {notificationChannels.map(channel => (
          <NotificationChannelComponent
            key={channel}
            channel={channel}
            action={action}
            getSetting={getSetting}
            updateSetting={updateSetting}
            isDisabled={isDisabled}
          />
        ))}
      </TwRow>
    )
  }

const taskColTitles = () => [
  Lang.msg.profilePage.event,
  Lang.msg.profilePage.chatNotification,
  Lang.msg.profilePage.soundNotification,
  Lang.msg.profilePage.browserNotification,
  Lang.msg.profilePage.mobileNotification
]

const taskSettingsRows: () => {
  title: string
  action: TaskNotificationAction
}[] = () => [
  {title: Lang.msg.profilePage.newChatMessage, action: 'newMessage'},
  {title: Lang.msg.profilePage.mentionChatMessage, action: 'newMention'},
  {title: Lang.msg.profilePage.deadlineNotification, action: 'deadline'},
  {title: Lang.msg.profilePage.othersDeadline, action: 'othersDeadline'},
  {title: Lang.msg.profilePage.taskCompleted, action: 'gtd'},
  {title: Lang.msg.profilePage.taskMoved, action: 'move'},
  {title: Lang.msg.profilePage.selfAssignment, action: 'selfAssigned'},
  {title: Lang.msg.profilePage.otherAssignment, action: 'otherAssigned'}
]

const chatSettingsRows: () => {
  title: string
  action: ChatNotificationAction
}[] = () => [
  {title: Lang.msg.profilePage.groupChatMessage, action: 'groupChatMessage'},
  {
    title: Lang.msg.profilePage.mentionInGroupChat,
    action: 'mentionInGroupChat'
  },
  {title: Lang.msg.profilePage.messageInChat, action: 'messageInChat'}
]

export const createToggleTaskNotificationSetting =
  (
    updater: (
      update: (
        settings: DeepReadonly<NotificationSettings>
      ) => NotificationSettings
    ) => void
  ) =>
  (action: TaskNotificationAction, channel: NotificationChannel) =>
  (): void =>
    updater(settings => {
      const prevValue = getNotificationSetting(settings, action, channel)

      let actionSettings = settings[action] || {}
      actionSettings = {...actionSettings, [channel]: !prevValue}

      return {
        ...settings,
        [action]: actionSettings
      }
    })

export const Notifications = observer(() => {
  useSyncEventUpdateEffect('profile', {
    debounceTime: 50,
    filter: globals.currentUserId
  })
  ChatSettings.useUpdateEffect()
  const chatSettings = ChatSettings.getSettings()

  const settings = useNotificationSettings()
  const setSettings = useCallback(
    (updater: (settings: NotificationSettings) => NotificationSettings) => {
      const prev = NotificationSettingsService.getNotificationSettings()
      NotificationSettingsService.setNotificationSettings(updater(prev))
    },
    []
  )

  const getSetting = (
    action: TaskNotificationAction,
    channel: NotificationChannel
  ) => {
    return getNotificationSetting(settings, action, channel)
  }
  const updateSetting = createToggleTaskNotificationSetting(setSettings)
  const isSettingDisabled = (
    action: TaskNotificationAction,
    channel: NotificationChannel
  ) => NotificationSettingsService.getDisabledSetting(action, channel)

  const TaskSettingRow = taskSettingRow(
    getSetting,
    updateSetting,
    isSettingDisabled
  )

  return (
    <TwTable>
      <TwTitle>{Lang.msg.profilePage.tasks}</TwTitle>
      <div className="mb-24 flex gap-8 justify-between text-sm-regular text-primary bg-background-hover rounded-8 p-12">
        <span>{Lang.msg.hideSystemMessages}:</span>
        <Switcher
          active={!chatSettings.showReadSystemMessages}
          onClick={() => {
            ChatSettings.setSettings({
              showReadSystemMessages: !chatSettings.showReadSystemMessages
            })
          }}
        />
      </div>
      <TwHeader>
        {taskColTitles().map(title => (
          <TwHeaderCol key={title}>{title}</TwHeaderCol>
        ))}
      </TwHeader>
      {taskSettingsRows().map(x => (
        <TaskSettingRow key={x.action} action={x.action} title={x.title} />
      ))}
      <TwDivider />

      <TwTitle>{Lang.msg.profilePage.chats}</TwTitle>
      <TwHeader>
        {taskColTitles().map(title => (
          <TwHeaderCol key={title}>{title}</TwHeaderCol>
        ))}
      </TwHeader>
      {chatSettingsRows().map(x => (
        <TaskSettingRow key={x.action} action={x.action} title={x.title} />
      ))}
    </TwTable>
  )
})
